import React, { useEffect, useState } from "react";
import { auth, db } from "./firebase"; // Firebase-Integration
import { Link, useNavigate } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import "./assets/style.css";

const TOTAL_PAGES = 40; // Gesamte Anzahl der Seiten

// Fortschrittsbalken-Logik (Halbkreis)
function progressBar(score) {
  const radius = 65;
  const dashArray = (Math.PI * radius * score) / 100;

  return (
    <svg width="200" height="120">
      <circle
        cx="100"
        cy="100"
        r={radius}
        fill="none"
        strokeWidth="25"
        strokeLinecap="round"
        strokeDashoffset={-1 * Math.PI * radius}
        strokeDasharray={`${dashArray} 10000`}
        stroke="#e5e5e5"
      ></circle>
      <defs>
        <linearGradient id="score-gradient">
          <stop offset="0%" stopColor="red" />
          <stop offset="50%" stopColor="orange" />
          <stop offset="100%" stopColor="green" />
        </linearGradient>
      </defs>
      <circle
        cx="100"
        cy="100"
        r={radius}
        fill="none"
        strokeWidth="25"
        strokeLinecap="round"
        strokeDashoffset={-1 * Math.PI * radius}
        strokeDasharray={`${dashArray} 10000`}
        stroke="url(#score-gradient)"
      ></circle>
    </svg>
  );
}

// ScoreMeter-Komponente (Anzeigen des Fortschritts in Prozent)
function ScoreMeter({ score }) {
  return (
    <div className="score-wrap">
      <div className="score">
        <div className="score-bar">
          <div className="placeholder">{progressBar(100)}</div>
          <div className="score-circle">{progressBar(score)}</div>
        </div>
        <div className="score-value">
          <div className="score-name"></div>
          <div className="score-number">{Math.round(score)}%</div>
        </div>
      </div>
    </div>
  );
}

// CourseOverview-Komponente
const CourseOverview = () => {
  const [userData, setUserData] = useState(null);
  const [completedChapters, setCompletedChapters] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        fetchUserData(user.uid);
      } else {
        setUserData(null);
        navigate("/cybersecurity/login");
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  const fetchUserData = async (userId) => {
    try {
      const userDoc = await db.collection("cybersecurityProgress").doc(userId).get();
      if (userDoc.exists) {
        const data = userDoc.data();
        setUserData(data);
        setCompletedChapters(data.completedChapters || []);
      }
    } catch (error) {
      console.error("Fehler beim Abrufen der Benutzerdaten:", error);
    }
  };

 const [userName, setUserName] = useState("");

useEffect(() => {
  const unsubscribe = auth.onAuthStateChanged(async (user) => {
    if (!user) {
      setUserName("");
      return;
    }
    try {
      // Vornamen aus dem Studienportal-User-Doc lesen (Single Source of
      // Truth fuer Stammdaten). Die Firestore-Rule erlaubt Lese-Zugriff
      // auf das eigene users/{uid}-Dokument.
      const portalUserDoc = await db.collection("users").doc(user.uid).get();
      const fullName = portalUserDoc.exists
        ? portalUserDoc.data().displayName || ""
        : user.displayName || "";
      setUserName(fullName.trim().split(/\s+/)[0] || "");
    } catch (error) {
      console.error("Fehler beim Laden der Benutzerdaten:", error);
      const fallback = user.displayName || "";
      setUserName(fallback.trim().split(/\s+/)[0] || "");
    }
  });
  return () => unsubscribe();
}, []);

  const handleLogout = () => {
    auth
      .signOut()
      .then(() => {
        navigate("/cybersecurity/login");
      })
      .catch((error) => {
        console.error("Logout fehlgeschlagen:", error);
      });
  };

  const progress = (completedChapters.length / TOTAL_PAGES) * 100;

  // Kapitel- und Subchapter-Daten
  const chapters = [
    {
      title: "Kapitel 1: Was ist Cybersecurity?",
      imageUrl: "../images/ov-cybersecurity.png",
      subchapters: [
        { title: "Einleitung", link: "/cybersecurity/1_1" },
        { title: "Meilensteine in der Cybersecurity", link: "/cybersecurity/1_2" },
        { title: "Die CIA-Tirade", link: "/cybersecurity/1_3" },
        { title: "Zugriffsmanagement", link: "/cybersecurity/1_4" },
        { title: "Wer sind die Angreifer?", link: "/cybersecurity/1_5" },
      ],
    },
    {
      title: "Kapitel 2: Sicherheitslücken und Schwachstellen",
      imageUrl: "/images/ov-sicherheitsluecken.webp",
      subchapters: [
        { title: "Was sind Sicherheitslücken und Schwachstellen?", link: "/cybersecurity/2_1" },
        { title: "Der Mensch als Schwachstelle", link: "/cybersecurity/2_2" },
        { title: "Passwort-Richtlinien", link: "/cybersecurity/2_3" },
        { title: "Phishing", link: "/cybersecurity/2_4" },
        { title: "Social Engineering Attacks", link: "/cybersecurity/2_5" },
      ],
    },
    {
      title: "Kapitel 3: Angriffe und Risiken",
      imageUrl: "/images/ov-bedrohungen.png",
      subchapters: [
        { title: "Malware", link: "/cybersecurity/3_1" },
        { title: "Ransomware", link: "/cybersecurity/3_2" },
        { title: "Exploits von Sicherheitslücken", link: "/cybersecurity/3_3" },
        { title: "DOS und DDOS Angriffe", link: "/cybersecurity/3_4" },
        { title: "Man-in-the-Middle (MitM)", link: "/cybersecurity/3_5" },
      ],
    },
    {
      title: "Kapitel 4: Netzwerke und Netzwerksicherheit",
      imageUrl: "/images/ov-netzwerk.png",
      subchapters: [
        { title: "Was ist ein Netzwerk?", link: "/cybersecurity/4_1" },
        { title: "Netzwerkarchitektur", link: "/cybersecurity/4_2" },
        { title: "Netzwerksicherheit", link: "/cybersecurity/4_3" },
        { title: "Datenverschlüsselung", link: "/cybersecurity/4_4" },
        { title: "Incident Response und Disaster Recovery", link: "/cybersecurity/4_5" },
      ],
    },
    {
      title: "Kapitel 5: Endpunktsicherheit",
      imageUrl: "/images/ov-endpoint.png",
      subchapters: [
        { title: "Was sind Endpunkte?", link: "/cybersecurity/5_1" },
        { title: "BYOD (Bring Your Own Device)", link: "/cybersecurity/5_2" },
        { title: "IoT-Geräte ", link: "/cybersecurity/5_3" },
        { title: "Mobile Endpunktsicherheit", link: "/cybersecurity/5_4" },
        { title: "UEM und Endpunktintelligenz", link: "/cybersecurity/5_5" },
      ],
    },
    {
      title: "Kapitel 6: Sicheres Cloud-Computing",
      imageUrl: "/images/ov-cloud.png",
      subchapters: [
        { title: "Was ist Cloud-Computing?", link: "/cybersecurity/6_1" },
        { title: "Grundlagen der Cloud-Sicherheit", link: "/cybersecurity/6_2" },
        { title: "Cloud-Sicherheitsstandards", link: "/cybersecurity/6_3" },
        { title: "Notfallmanagement für die Cloud", link: "/cybersecurity/6_4" },
        {
          title: "Zukunft des Cloud-Computing",
          link: "/cybersecurity/6_5",
        },
      ],
    },
    {
      title: "Kapitel 7: IT-Risikomanagement",
      imageUrl: "/images/ov-risiko.png",
      subchapters: [
        { title: "Risikomanagement in der Cybersecurity", link: "/cybersecurity/7_1" },
        { title: "Risikoidentifikation", link: "/cybersecurity/7_2" },
        { title: "Risikobewertung", link: "/cybersecurity/7_3" },
        { title: "Risikosteuerung", link: "/cybersecurity/7_4" },
        { title: "Ausgewählte Instrumente der IT-Risikosteuerung",link: "/cybersecurity/7_5",
        },
      ],
    },
    {
      title: "Kapitel 8: Regulatorische Anforderungen",
      imageUrl: "/images/ov-risikomanagement.png",
      subchapters: [
        { title: "KRITIS", link: "/cybersecurity/8_1" },
        {
          title: "NIS2",
          link: "/cybersecurity/8_2",
        },
        { title: "DORA", link: "/cybersecurity/8_3" },
        {
          title: "Umsetzungsstrategien",
          link: "/cybersecurity/8_4",
        },
        {
          title: "Cybersecurity Entscheidungsbaum",
          link: "/cybersecurity/8_5",
        },
      ],
    },
  ];

  return (
    <div className="container">
      <div className="container py-5">
        <img
          src="/images/zebbs_Logo.svg"
          class="d-block mx-auto mb-4"
          alt=""
          style={{ width: "20%" }}
        />
        <div className="d-flex justify-content-between align-items-center mb-5 p-3 bg-body-tertiary rounded-3">
          {/* Breadcrumbs */}
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb breadcrumb-chevron mb-0">
              {/* Startseite (Home) */}
              <li className="breadcrumb-item">
                <Link className="link-body-emphasis" to="/courses">
                  <i className="bi bi-house-door-fill"></i>
                  <span className="visually-hidden">Home</span>
                </Link>
              </li>

              {/* Library Link */}
              <li className="breadcrumb-item">
                <Link
                  className="link-body-emphasis fw-semibold text-decoration-none"
                  to="/courses"
                >
                  Online-Kurs Cybersecurity
                </Link>
              </li>
            </ol>
          </nav>

          {/* Logout-Button */}
          <button className="btn btn-outline-dark" onClick={handleLogout}>
            Logout
          </button>
        </div>

        <div className="container">
          <div className="row justify-content-center">
            <div className="col-md-8">
              {/* Fortschrittsanzeige */}
              <div className="progress">
                <div className="progress-container">
                  <div className="progress-item">
                    <h4>
                      {userName && <h4>Hallo {userName},</h4>} hier siehst du
                      deinen Arbeitsfortschritt:
                    </h4>
                    <ScoreMeter score={progress} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="row">
        <h3 className="mb-5">Kursübersicht</h3>
        {chapters.map((chapter, index) => (
          <div className="col-12 col-sm-6 col-md-4 col-lg-3 mb-4" key={index}>
            <div className="card">
              <img
                src={chapter.imageUrl}
                className="card-img-top "
                alt={chapter.title}
              />
              <div className="card-body">
                <h5 className="card-title">{chapter.title}</h5>
                <ul className="list-group list-group-flush">
                  {chapter.subchapters.map((subchapter, subIndex) => (
                    <li className="list-group-item" key={subIndex}>
                      <Link
                        to={subchapter.link}
                        style={{
                          textDecoration: completedChapters.includes(
                            subchapter.link.replace("/cybersecurity/", "")
                          )
                            ? "none"
                            : "underline",
                          color: completedChapters.includes(
                            subchapter.link.replace("/cybersecurity/", "")
                          )
                            ? "gray"
                            : "black",
                        }}
                      >
                        {subchapter.title}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
                       </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CourseOverview;
